import React, { useState } from 'react';
import ProfilePictureUploader from './ProfilePictureUploader';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  InputLabel,
  TextField,
} from '@mui/material';
import { changePassword, updateCurrentUserInfo } from '../../../services/auth';
import { enqueueSnackbar } from 'notistack';
import posthog from 'posthog-js';
import { RootState } from '../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { AlertTriangle, ExternalLink } from 'react-feather';
import { useHandleResetProgress } from '../../../utils/resetProgress';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate } from 'react-router-dom';
import { deleteUser } from '../../../services/users';
import { logout } from '../../../slices/auth/authSlice';
import { TriangleRight } from '../../../assets/svgs/TriangleRight';
import { TriangleDown } from '../../../assets/svgs/TriangleDown';
import CustomModal from '../../Global/CustomModal';
import SchoolSelector from '../../Global/SchoolSelector';
import { hapticsImpactLight } from '../../../utils/haptics';
import GoogleIcon from '../../../assets/google-g.svg';
import AppleIcon from '../../../assets/apple.svg';

const AccountProfile: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const currentAuth = useSelector((state: RootState) => state.auth);

  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [dangerZoneModalIsOpen, setDangerZoneModalIsOpen] = useState(false);
  const [dangerZoneType, setDangerZoneType] = useState('');

  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [editName, setEditName] = useState(currentUser?.name || '');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleResetProgress = useHandleResetProgress();

  useHotkeys(
    'esc',
    () => {
      if (!dangerZoneModalIsOpen) {
        navigate('/');
      }
    },
    {},
    [dangerZoneModalIsOpen]
  );

  const isChangePasswordValid = () => {
    return (
      newPassword === confirmNewPassword &&
      newPassword.length >= 8 &&
      oldPassword !== newPassword
    );
  };

  const handleChangePassword = async () => {
    const response = await changePassword(
      currentAuth.authToken as string,
      oldPassword,
      newPassword
    );
    if (response.error) {
      setPasswordChangeError(response.error);
    } else {
      enqueueSnackbar('Password updated.', {
        autoHideDuration: 3000,
      });
    }
    setIsChangingPassword(false);
    setOldPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
  };

  const handleDangerZoneButton = (type: string) => {
    setDangerZoneModalIsOpen(true);
    setDangerZoneType(type);
  };

  const handleSignOut = () => {
    dispatch(logout());
    navigate('/auth');
  };

  const handleDangerZoneAction = async (type: string) => {
    if (type === 'delete' && dangerZoneType === 'delete') {
      if (currentUser?.id) {
        const response = await deleteUser(currentUser?.id);
        if (response?.status === 204) {
          posthog?.capture('user_deleted_account');
          handleSignOut();
        } else {
          enqueueSnackbar(
            'Error deleting account. Please reload the page and try again.'
          );
        }
      }
    } else if (type === 'reset' && dangerZoneType === 'reset') {
      handleResetProgress();
    }
    setDangerZoneModalIsOpen(false);
  };

  const handleSaveName = async () => {
    const updateUser = await updateCurrentUserInfo({ name: editName });
    if (updateUser) {
      enqueueSnackbar('Name updated.', { autoHideDuration: 3000 });
      posthog?.capture('user_updated_name');
    } else {
      enqueueSnackbar(
        'Error updating name. Please reload the page and try again.'
      );
    }
  };

  return (
    <>
      <h1>Profile</h1>
      <div className="block block--settings block--settings--personal-info">
        <div className="block_body">
          <h3>Personal Info</h3>
          <div className="personal-info-container">
            <ProfilePictureUploader />
            <div className="field">
              <InputLabel id="name" className="m-b-0-50">
                Display Name
              </InputLabel>
              <TextField
                id="name"
                variant="outlined"
                value={editName}
                fullWidth
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setEditName(event.target.value);
                }}
              />
            </div>
          </div>
        </div>
        <div className="block_footer">
          <div className="helper">5MB image limit.</div>
          <button
            className="button"
            onClick={() => {
              handleSaveName();
              hapticsImpactLight();
            }}
            disabled={editName === currentUser?.name}
          >
            Update
          </button>
        </div>
      </div>
      <div className="block block--settings">
        <div className="block_body">
          <h3>School</h3>
          <SchoolSelector />
        </div>
      </div>
      <div className="block block--settings">
        <div className="block_body">
          <h3>Email</h3>
          <div className="field">
            <div className="value">{currentUser?.email}</div>
          </div>
        </div>
        <div className="block_footer">
          <div
            className="helper"
            style={{ marginRight: 'auto', marginLeft: '0' }}
          >
            This cannot be changed.
          </div>
        </div>
      </div>
      <div className="block block--settings">
        <div className="block_body">
          <h3>Password</h3>
          {!isChangingPassword && (
            <>
              {currentUser?.oauth && (
                <p>Your password is managed by {currentUser?.oauthStrategy}.</p>
              )}
              {!currentUser?.oauth && <div>···········</div>}
            </>
          )}
          {isChangingPassword && (
            <div>
              {passwordChangeError && (
                <p style={{ color: 'var(--error)' }}>{passwordChangeError}</p>
              )}
              <p>Change your password</p>
              <TextField
                className="m-y-0-75"
                id="oldPassword"
                label="Current Password"
                variant="outlined"
                value={oldPassword}
                type="password"
                fullWidth
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setOldPassword(event.target.value);
                }}
              />
              <TextField
                className="m-y-0-75"
                id="newPassword"
                label="New Password"
                variant="outlined"
                value={newPassword}
                type="password"
                fullWidth
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setNewPassword(event.target.value);
                }}
              />
              <TextField
                className="m-y-0-75"
                id="confirmNewPassword"
                label="Confirm Password"
                variant="outlined"
                value={confirmNewPassword}
                type="password"
                fullWidth
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setConfirmNewPassword(event.target.value);
                }}
              />
            </div>
          )}
        </div>
        <div className="block_footer">
          {!currentUser?.oauth && (
            <>
              {!isChangingPassword && (
                <button
                  className="button button"
                  onClick={() => {
                    setIsChangingPassword(true);
                    hapticsImpactLight();
                  }}
                >
                  Update
                </button>
              )}
              {isChangingPassword && (
                <button
                  className="button button--secondary"
                  onClick={() => {
                    setIsChangingPassword(false);
                    hapticsImpactLight();
                  }}
                >
                  Cancel
                </button>
              )}
              {isChangingPassword && (
                <button
                  className="button"
                  onClick={() => {
                    handleChangePassword();
                    hapticsImpactLight();
                  }}
                  disabled={!isChangePasswordValid()}
                >
                  Update
                </button>
              )}
            </>
          )}
          {currentUser?.oauth && (
            <>
              {currentUser?.oauthStrategy === 'Google' && (
                <a
                  className="button"
                  style={{ width: 'fit-content' }}
                  href="https://myaccount.google.com/signinoptions/password"
                  target="_blank"
                  onClick={hapticsImpactLight}
                >
                  <img src={GoogleIcon} alt="Google Logo" />
                  Manage Password
                  <ExternalLink
                    style={{
                      height: '1rem',
                      marginLeft: '.125rem',
                    }}
                  />
                </a>
              )}
              {currentUser?.oauthStrategy === 'Apple' && (
                <a
                  className="button"
                  style={{ width: 'fit-content' }}
                  href="https://support.apple.com/en-us/101567#:~:text=Change%20your%20Apple%20ID%20password%20on%20your%20iPhone%2C%20iPad%2C%20Apple,and%20confirm%20the%20new%20password."
                  target="_blank"
                  onClick={hapticsImpactLight}
                >
                  <img src={AppleIcon} alt="Apple Logo" />
                  Manage Password
                  <ExternalLink
                    style={{
                      height: '1rem',
                      marginLeft: '.125rem',
                    }}
                  />
                </a>
              )}
            </>
          )}
        </div>
      </div>
      <Accordion>
        <AccordionSummary
          aria-controls="adv-study-content"
          id="adv-study-header"
          sx={{ padding: 0 }}
        >
          <div className="row">
            <div className="icon">
              <TriangleRight classes="expand-icon expand-icon--closed" />
              <TriangleDown classes="expand-icon expand-icon--open" />
            </div>
            <span className="button button--link p-l-0">
              Advanced Account Settings
            </span>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          {currentUser?.role === 'admin' && (
            <div className="block block--settings block--danger">
              <div className="block_body">
                <h3>Reset Scheduling</h3>
                <p className="m-b-1-50">
                  This action wipes all your scheduling data and resets your
                  study progress to zero. This cannot be undone.
                </p>
                <button
                  className="button button--error"
                  onClick={() => handleDangerZoneButton('reset')}
                >
                  Reset Scheduling
                </button>
              </div>
            </div>
          )}
          <div className="block block--settings block--danger">
            <div className="block_body">
              <h3>Delete Account</h3>
              <p className="m-b-1-50">
                This action fully deletes your account including your scheduling
                data. You will be logged out. This cannot be undone.
              </p>
              <button
                className="button button--error"
                onClick={() => handleDangerZoneButton('delete')}
              >
                Delete Account
              </button>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
      <CustomModal
        open={dangerZoneModalIsOpen}
        onClose={() => setDangerZoneModalIsOpen(false)}
        name="danger-zone"
        glass={true}
      >
        <div className="modal_header">
          {dangerZoneType === 'reset' && <h2>Reset Scheduling</h2>}
          {dangerZoneType === 'delete' && <h2>Delete Account</h2>}
        </div>
        <div className="modal_content">
          {dangerZoneType === 'reset' && (
            <p>
              <AlertTriangle
                style={{
                  color: 'var(--error',
                  height: '1rem',
                  transform: 'translateY(2px)',
                }}
              />
              <strong style={{ color: 'var(--error' }}>
                PROCEED WITH CAUTION
              </strong>
              <br />
              <br />
              This action wipes all your scheduling data. This resets your study
              progress to zero. This cannot be undone.
            </p>
          )}
          {dangerZoneType === 'delete' && (
            <p>
              <AlertTriangle
                style={{
                  color: 'var(--error',
                  height: '1rem',
                  transform: 'translateY(2px)',
                }}
              />
              <strong style={{ color: 'var(--error' }}>
                PROCEED WITH CAUTION
              </strong>
              <br />
              <br />
              This action will permanently delete your account, including all
              scheduling data. Any active subscription will be canceled, and you
              will be logged out. This cannot be undone.
            </p>
          )}
        </div>
        <div className="modal_actions">
          <button
            className="button button--secondary"
            onClick={() => {
              setDangerZoneModalIsOpen(false);
              hapticsImpactLight();
            }}
          >
            Cancel
          </button>
          <button
            className="button button--error"
            onClick={() => {
              handleDangerZoneAction(dangerZoneType);
              hapticsImpactLight();
            }}
          >
            {dangerZoneType === 'reset' ? 'Reset Scheduling' : 'Delete Account'}
          </button>
        </div>
      </CustomModal>
    </>
  );
};

export default AccountProfile;
